import React, { useState, useCallback } from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu/';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item/';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler/';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';

import { AvailableActions } from './available-actions';
import { display, availableStatusIds } from './constants';
import { scanSuccessToActionCreatorMap } from './handlers';
import { ScanToSearchHotkey } from './hotkey';
import style from './index.module.less';
import { messages } from './messages';
import { getBatchListsFromState } from '../../../containers/batches/get-batch-collections-from-state';
import PrepayDutyAlertModal from '../../../fulfillment-planner/duties-and-taxes/alert-modal';
import { Recipient } from '../../../fulfillment-planner/duties-and-taxes/constants';
import {
  getUserSettingPrepayDutyAlert,
  createUserSettingPrepayDutyAlertFromSetting,
  shouldShowPrepayDutyAlert,
  filterDutiesOptions,
} from '../../../fulfillment-planner/duties-and-taxes/helpers';
import { useDeprecatedLocalStorage, SCAN_TO_SEARCH_MODAL_PERSISTENCE_PREFERENCE } from '../../../hooks/use-local-storage';
import { useModal } from '../../../hooks/use-modal';
import { bulkLoadFulfillmentPlanIds } from '../../../store/bulkload/bulkload.api';
import { getPrepaidDuty } from '../../../store/csw/csw.helpers';
import { updateFulfillmentPlanAdditionalOptions } from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import * as UserActions from '../../../store/user/user.actions';

const useShouldShowPrepayDutyAlert = () => {
  const shouldPromptBasedOnSettings = useSelector(shouldShowPrepayDutyAlert);
  const existingSetting = useSelector(getUserSettingPrepayDutyAlert);
  const dispatch = useDispatch();

  const getAvailableDutiesOptions = useCallback(
    (fulfillmentPlans) => {
      if (fulfillmentPlans?.length !== 1) {
        return null;
      }
      const fulfillmentPlan = fulfillmentPlans[0];
      const availableDutiesOptions = filterDutiesOptions(fulfillmentPlan?.labelConfiguration?.availableShippingOptions);
      const prepaidDuty = getPrepaidDuty({
        otherShippingOptions: fulfillmentPlan?.labelConfiguration?.otherShippingOptions,
      });
      if (shouldPromptBasedOnSettings) {
        if (availableDutiesOptions.length) {
          if (prepaidDuty === Recipient) {
            return availableDutiesOptions;
          }
          const updatedSetting = createUserSettingPrepayDutyAlertFromSetting(existingSetting, {
            createdPrepayDutyOn: moment().toISOString(),
          });
          dispatch(UserActions.updateUserSetting(updatedSetting));
        }
      }
      return null;
    },
    [shouldPromptBasedOnSettings, existingSetting, dispatch],
  );

  return getAvailableDutiesOptions;
};

/**
 * @typedef {import('ui-components/prop-types/modal-framework-methods').ModalFrameworkMethodsPropType} ModalFrameworkMethodsPropType
 */

/**
 * @param {object} props
 * @param {() => void?} props.onClose
 * @param {ModalFrameworkMethodsPropType?} props.modalFrameworkMethods
 * @returns {React.JSX.Element}
 */
export const OrderScanToSearchModal = ({ onClose, modalFrameworkMethods }) => {
  // This modal can be opened via the grid header buttons which uses the modal service to launch the modal,
  // or via the hotkey that uses the Modal Portal.
  const close = onClose ?? modalFrameworkMethods?.closeModal;
  const dispatch = useDispatch();
  const history = useHistory();

  const { open: openPrepayAlertModal, close: closePrepayAlertModal, isOpen: isPrepayAlertModalOpen } = useModal();
  const getAvailableDutiesOptions = useShouldShowPrepayDutyAlert();
  const [postAlertScanAction, setPostAlertScanAction] = useState(null);
  const [availableDutiesOptions, setAvailableDutiesOptions] = useState([]);

  const onPrepayDutyChange = useCallback(
    (value) => {
      dispatch(updateFulfillmentPlanAdditionalOptions(postAlertScanAction.fulfillmentPlanIds[0], [value]));
    },
    [dispatch, postAlertScanAction],
  );

  const { myBatches } = useSelector(getBatchListsFromState);
  const assignToUserId = useSelector(Global.selectUserCurrentUserId);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState(availableStatusIds[1]);
  const [selectedBatch, setSelectedBatch] = useState(myBatches[0] ?? {});
  const [selectedScanActionId, setSelectedScanActionId] = useState(display);
  const [keepWindowOpen, setKeepWindowOpen] = useDeprecatedLocalStorage(SCAN_TO_SEARCH_MODAL_PERSISTENCE_PREFERENCE, false);

  const onPrepayDutyAlertContinue = () => {
    dispatch(postAlertScanAction);
    if (!keepWindowOpen) {
      close();
    }
  };

  const selectedFulfillmentPlanSetId = selectedBatch?.fulfillmentPlanSetId;

  const onSelectedBatchChange = useCallback(
    (currentFulfillmentPlanSetId) => {
      const newSelectedBatch = myBatches.find(({ fulfillmentPlanSetId }) => fulfillmentPlanSetId === currentFulfillmentPlanSetId);
      setSelectedBatch(newSelectedBatch);
    },
    [myBatches],
  );

  const toggleKeepWindowOpen = useCallback(() => setKeepWindowOpen((windowOpen) => !windowOpen), [setKeepWindowOpen]);

  const onScanSuccess = useCallback(
    async (scanData) => {
      const scanAction = scanSuccessToActionCreatorMap[selectedScanActionId](scanData, {
        history,
        assignToUserId,
        fulfillmentPlanSetId: selectedFulfillmentPlanSetId,
        orderStatus: selectedOrderStatus,
      });
      if (scanAction) {
        if (scanData?.orderNumber) {
          // this will populate the entities reducer through the axios response interceptor
          return bulkLoadFulfillmentPlanIds(scanAction.fulfillmentPlanIds)
            .then((data) => {
              const _availableDutiesOptions = getAvailableDutiesOptions(data.fulfillmentPlans);
              if (_availableDutiesOptions) {
                setPostAlertScanAction(scanAction);
                setAvailableDutiesOptions(_availableDutiesOptions);
                openPrepayAlertModal();
                return;
              }

              dispatch(scanAction);
              if (!keepWindowOpen) {
                close();
              }
            })
            .catch(noop);
        }
        dispatch(scanAction);
      }
      if (!keepWindowOpen) {
        close();
      }
    },
    [
      dispatch,
      getAvailableDutiesOptions,
      selectedScanActionId,
      close,
      keepWindowOpen,
      assignToUserId,
      history,
      openPrepayAlertModal,
      selectedFulfillmentPlanSetId,
      selectedOrderStatus,
    ],
  );

  const onOrderStatusChange = useCallback((option) => {
    setSelectedOrderStatus(option);
    setSelectedScanActionId(display); // Certain options are disabled for different statuses, display is always available.
  }, []);

  return (
    <>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.modalTitle} />
        </ModalHeader>
        <ModalBody className={style['order-scan-to-search-modal-body']}>
          <div className={style['section-header']}>
            <FormattedMessage {...messages.searchOrderStatusLabel} />
          </div>
          <DropdownMenu
            className={style['order-status']}
            dropdownMenuShouldHaveWidthOfButton
            onClose={
              () => document.activeElement.blur() /** Otherwise this element will hijack the scan, This is also used in AvailableActions. */
            }
          >
            <DropdownMenuToggler className={style.toggler}>
              <FormattedMessage {...messages[selectedOrderStatus]} />
            </DropdownMenuToggler>
            {availableStatusIds.map((option) => (
              <DropdownMenuItem key={option} name={option} onClick={onOrderStatusChange} selected={option === selectedOrderStatus}>
                <FormattedMessage {...messages[option]} />
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
          <div className={style['section-header']}>
            <FormattedMessage {...messages.whenOrderFoundLabel} />
          </div>
          <AvailableActions
            myBatches={myBatches}
            onActionAfterScanChange={setSelectedScanActionId}
            onSelectedBatchChange={onSelectedBatchChange}
            selectedActionId={selectedScanActionId}
            selectedBatch={selectedBatch}
            selectedOrderStatus={selectedOrderStatus}
          />
          <ScanToSearchHotkey onSuccessfulScan={onScanSuccess} selectedOrderStatus={selectedOrderStatus} />
        </ModalBody>
        <ModalFooter className={style.footer}>
          <Checkbox
            checked={keepWindowOpen}
            label={<FormattedMessage {...messages.keepWindowOpenLabel} />}
            onChange={toggleKeepWindowOpen}
          />
        </ModalFooter>
      </Modal>
      {isPrepayAlertModalOpen ? (
        <PrepayDutyAlertModal
          availableDutiesOptions={availableDutiesOptions}
          onChange={onPrepayDutyChange}
          onClose={closePrepayAlertModal}
          onContinue={onPrepayDutyAlertContinue}
        />
      ) : null}
    </>
  );
};
